import "./Filters.css";

function Filters({
  chargerType,
  setChargerType,
  locations,
  selectedLocations,
  setSelectedLocations,

  revOnly,
  setRevOnly,

  startDate,
  setStartDate,
  endDate,
  setEndDate,
  quickRange,
  setQuickRange,
  grouping,
  setGrouping,
  onOpenMap
}) {

  // IDs for the locations currently visible under the selected charger type.
  const visibleLocationIds = new Set(
    locations.map(
      (location) => location.chargingStationId
    )
  );

  // Number of currently visible locations that are selected.
  const visibleSelectedCount = selectedLocations.filter(
    (id) => visibleLocationIds.has(id)
  ).length;

  // True when every currently visible location is selected.
  const allVisibleSelected =
    locations.length > 0 &&
    visibleSelectedCount === locations.length;


  // Adds or removes a location from the selected locations list.
  const handleLocationChange = (locationId) => {
    setSelectedLocations((current) => {
      if (current.includes(locationId)) {
        return current.filter(
          (id) => id !== locationId
        );
      }

      return [
        ...current,
        locationId
      ];
    });
  };


  // Selects every location currently visible for the active charger type.
  // Existing selections for the other charger type are preserved.
  const handleSelectAll = () => {
    const currentVisibleIds = locations.map(
      (location) => location.chargingStationId
    );

    setSelectedLocations((current) => [
      ...new Set([
        ...current,
        ...currentVisibleIds
      ])
    ]);
  };


  // Deselects every location currently visible for the active charger type.
  // Selections belonging to the other charger type are preserved.
  const handleDeselectAll = () => {
    setSelectedLocations((current) =>
      current.filter(
        (id) => !visibleLocationIds.has(id)
      )
    );
  };


  // Changes charger type without clearing existing location selections.
  const handleChargerTypeChange = (type) => {
    setChargerType(type);
  };


  // Text displayed in the status badge.
  const locationStatus =
    locations.length === 0
      ? "No locations available"
      : visibleSelectedCount === 0
      ? "No locations selected"
      : allVisibleSelected
      ? "All locations"
      : `${visibleSelectedCount} selected`;


  return (
    <section className="filters-panel">

      {/* Header */}
      <div className="filters-header">
        <div>
          <span className="filters-eyebrow">
            Dashboard controls
          </span>

          <h1>
            Filter charging data
          </h1>
        </div>

        <span className="filters-status">
          {locationStatus}
        </span>
      </div>


      {/* Main two-column filter layout */}
      <div className="filters-layout">

        {/* LEFT COLUMN */}
        <div className="filters-column">

          {/* Charger type filter */}
          <div className="filter-section">
            <h2>
              Charger type
            </h2>

            <p className="filter-description">
              Choose which charger category to display.
            </p>

            <div className="charger-options">

              {["Both", "AC", "DC"].map((type) => (
                <label
                  key={type}
                  className={`radio-card ${
                    chargerType === type
                      ? "radio-card-active"
                      : ""
                  }`}
                >
                  <input
                    type="radio"
                    value={type}
                    checked={chargerType === type}
                    onChange={() =>
                      handleChargerTypeChange(type)
                    }
                  />

                  <span>
                    {type === "Both"
                      ? "Both"
                      : `${type} only`}
                  </span>
                </label>
              ))}

            </div>


            {/* REV-only filter */}
            <div className="rev-filter-row">
              <button
                type="button"
                className={`rev-only-button ${
                  revOnly
                    ? "rev-only-button-active"
                    : ""
                }`}
                onClick={() =>
                  setRevOnly((current) => !current)
                }
              >
                REV only
              </button>

              <span className="rev-filter-description">
                {revOnly
                  ? "Showing REV network charging data only."
                  : "Showing all available charging data."}
              </span>
            </div>
          </div>


          {/* Location selection */}
          <div className="filter-section locations-section">

            <div className="section-heading-row">

              <div>
                <h2>
                  Locations
                </h2>

                <p className="filter-description">
                  Select the charging locations to include in the dashboard.
                </p>
              </div>


              {/* Select / deselect controls */}
              <div className="location-selection-actions">

                <button
                  type="button"
                  className="location-action-button"
                  onClick={handleSelectAll}
                  disabled={
                    locations.length === 0 ||
                    allVisibleSelected
                  }
                >
                  Select all
                </button>

                <button
                  type="button"
                  className="location-action-button"
                  onClick={handleDeselectAll}
                  disabled={visibleSelectedCount === 0}
                >
                  Deselect all
                </button>

              </div>
            </div>


            {/* Location list */}
            <div className="locations-list">

              {locations.length === 0 ? (

                <div className="no-locations-message">
                  No charging locations match the current filters.
                </div>

              ) : (

                locations.map((location) => (

                  <label
                    key={`${location.chargingStationId}-${location.type}`}
                    className="location-option"
                  >

                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(
                        location.chargingStationId
                      )}
                      onChange={() =>
                        handleLocationChange(
                          location.chargingStationId
                        )
                      }
                    />


                    <span className="location-name">
                      {location.locationName}
                    </span>


                    <span
                      className={`charger-badge ${
                        location.type === "DC"
                          ? "charger-badge-dc"
                          : "charger-badge-ac"
                      }`}
                    >
                      {location.type}
                    </span>

                  </label>

                ))

              )}

            </div>


            {/* Location summary + map */}
            <div className="locations-map-actions">

              <div className="locations-map-summary">

                {locations.length === 0 ? (

                  "No locations available."

                ) : visibleSelectedCount === 0 ? (

                  "No locations selected."

                ) : allVisibleSelected ? (

                  "All available locations are selected."

                ) : (

                  `${visibleSelectedCount} ${
                    visibleSelectedCount === 1
                      ? "location"
                      : "locations"
                  } selected.`

                )}

              </div>


              <button
                type="button"
                className="view-map-button"
                onClick={onOpenMap}
              >
                <span aria-hidden="true">
                  ⌖
                </span>

                View on map
              </button>

            </div>

          </div>

        </div>


        {/* RIGHT COLUMN */}
        <div className="filters-column filters-column-right">

          {/* Custom date range */}
          <div className="filter-section">

            <h2>
              Time range
            </h2>

            <p className="filter-description">
              Select a custom start and end date.
            </p>


            <div className="date-inputs">

              <label className="field-group">

                <span>
                  From
                </span>

                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setQuickRange("");
                    setStartDate(
                      e.target.value
                    );
                  }}
                />

              </label>


              <label className="field-group">

                <span>
                  To
                </span>

                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setQuickRange("");
                    setEndDate(
                      e.target.value
                    );
                  }}
                />

              </label>

            </div>
          </div>


          {/* Quick range */}
          <div className="filter-section">

            <label className="field-group">

              <span className="field-title">
                Quick range
              </span>


              <select
                value={quickRange}
                onChange={(e) =>
                  setQuickRange(
                    e.target.value
                  )
                }
              >

                <option value="">
                  Custom range
                </option>

                <option value="last7">
                  Last 7 days
                </option>

                <option value="last30">
                  Last 30 days
                </option>

                <option value="thisMonth">
                  This month
                </option>

                <option value="lastMonth">
                  Last month
                </option>

                <option value="allTime">
                  All time
                </option>

              </select>

            </label>

          </div>


          {/* Grouping */}
          <div className="filter-section">

            <label className="field-group">

              <span className="field-title">
                Grouping
              </span>


              <select
                value={grouping}
                onChange={(e) =>
                  setGrouping(
                    e.target.value
                  )
                }
              >

                <option value="daily">
                  Daily
                </option>

                <option value="monthly">
                  Monthly
                </option>

                <option value="yearly">
                  Yearly
                </option>

              </select>

            </label>

          </div>

        </div>

      </div>

    </section>
  );
}

export default Filters;